import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Tab, Tabs, Spinner, Alert, Modal, Toast } from 'react-bootstrap';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../redux/store';
import {
    selectCandidateById,
    selectDocumentsForCandidate,
    selectNotesForCandidate,
    selectActivitiesForCandidate,
    selectDocumentsLoadingForCandidate,
    selectNotesLoadingForCandidate,
    selectActivitiesLoadingForCandidate,
    selectCandidateLoading,
    selectCandidateError
} from '../redux/selectors/candidateSelectors';
import {
    fetchCandidateById,
    fetchCandidateDocuments,
    fetchCandidateNotes,
    fetchCandidateActivities,
    uploadCandidateDocument,
    deleteCandidateDocument,
    addCandidateNote,
    updateCandidateNote,
    deleteCandidateNote,
    updateCandidate,
    changeCandidateStage
} from '../redux/slices/candidatesSlice';
import { handleCandidateStageChange as updateStageCount } from '../redux/slices/stagesSlice';
import { stageService } from '../services/api/stageService';
import { emailTemplateService } from '../services/api/emailTemplateService';
import { Stage, EmailLog } from '../types/types';
import DocumentList from '../components/document/DocumentList';
import DocumentUploader from '../components/document/DocumentUploader';
import AdvanceStageButton from '../components/candidate/AdvanceStageButton';
import CandidateForm from '../components/candidate/CandidateForm';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ActivityTimeline from '../components/candidate/ActivityTimeline';
import SendEmailModal from '../components/admin/SendEmailModal';
import NotesList from '../components/notes/NoteList';
import { Note } from '../types/types';

const ViewCandidatePage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    // Where the user opened this profile from; Back falls back to the candidate's stage list
    const backTo = (location.state as { from?: string } | null)?.from;

    // Redux selectors
    const candidate = useAppSelector(state => id ? selectCandidateById(state, id) : null);
    const documents = useAppSelector(state => id ? selectDocumentsForCandidate(state, id) : []);
    const notes = useAppSelector(state => id ? selectNotesForCandidate(state, id) : []);
    const activities = useAppSelector(state => id ? selectActivitiesForCandidate(state, id) : []);
    const candidateLoading = useAppSelector(selectCandidateLoading);
    const candidateError = useAppSelector(selectCandidateError);
    const documentsLoading = useAppSelector(state => id ? selectDocumentsLoadingForCandidate(state, id) : false);
    const notesLoading = useAppSelector(state => id ? selectNotesLoadingForCandidate(state, id) : false);
    const activitiesLoading = useAppSelector(state => id ? selectActivitiesLoadingForCandidate(state, id) : false);

    // Local state
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [uploadingDocuments, setUploadingDocuments] = useState<boolean>(false);
    const [showEditForm, setShowEditForm] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [dataFetched, setDataFetched] = useState<{ [key: string]: boolean }>({});
    const [showToast, setShowToast] = useState<boolean>(false);
    const [toastMessage, setToastMessage] = useState<string>('');
    const [toastVariant, setToastVariant] = useState<'success' | 'danger'>('success');

    // Email state
    const [showEmailModal, setShowEmailModal] = useState<boolean>(false);
    const [currentStage, setCurrentStage] = useState<Stage | null>(null);
    const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
    const [emailLogsLoading, setEmailLogsLoading] = useState<boolean>(false);
    const [showEmailPreviewModal, setShowEmailPreviewModal] = useState<boolean>(false);
    const [selectedEmailLog, setSelectedEmailLog] = useState<EmailLog | null>(null);
    const [emailPreviewHtml, setEmailPreviewHtml] = useState<string>('');
    const [emailPreviewLoading, setEmailPreviewLoading] = useState<boolean>(false);

    // Fetch candidate data once on mount
    useEffect(() => {
        if (id && !candidate && !candidateLoading && !dataFetched.candidate) {
            dispatch(fetchCandidateById(id))
                .unwrap()
                .catch((error) => {
                    console.log('fetchCandidateById ERROR:', error);
                });
            setDataFetched(prev => ({ ...prev, candidate: true }));
        }
    }, [id, candidate, candidateLoading, dataFetched.candidate, dispatch]);

    // Fetch documents once when candidate is loaded
    useEffect(() => {
        if (id && candidate && documents.length === 0 && !documentsLoading && !dataFetched.documents) {
            dispatch(fetchCandidateDocuments(id));
            setDataFetched(prev => ({ ...prev, documents: true }));
        }
    }, [id, candidate, documents.length, documentsLoading, dataFetched.documents, dispatch]);

    // Fetch notes once when candidate is loaded
    useEffect(() => {
        if (id && candidate && notes.length === 0 && !notesLoading && !dataFetched.notes) {
            dispatch(fetchCandidateNotes(id));
            setDataFetched(prev => ({ ...prev, notes: true }));
        }
    }, [id, candidate, notes.length, notesLoading, dataFetched.notes, dispatch]);

    // Fetch activities once when candidate is loaded
    useEffect(() => {
        if (id && candidate && activities.length === 0 && !activitiesLoading && !dataFetched.activities) {
            dispatch(fetchCandidateActivities(id));
            setDataFetched(prev => ({ ...prev, activities: true }));
        }
    }, [id, candidate, activities.length, activitiesLoading, dataFetched.activities, dispatch]);

    // Update stage information when candidate changes (only when stageId actually changes)
    useEffect(() => {
        const updateStageInfo = async () => {
            if (candidate?.stageId && (!currentStage || currentStage.id !== candidate.stageId)) {
                try {
                    const stageData = await stageService.getStageById(candidate.stageId);
                    setCurrentStage(stageData);
                } catch (error) {
                    console.error('Error fetching stage data:', error);
                }
            }
        };

        updateStageInfo();
    }, [candidate?.stageId, currentStage?.id]);

    // Fetch email logs once when candidate is loaded
    useEffect(() => {
        const fetchEmailLogs = async () => {
            if (id && candidate && !dataFetched.emailLogs) {
                try {
                    setEmailLogsLoading(true);
                    const logs = await emailTemplateService.getEmailLogs(id);
                    setEmailLogs(logs);
                    setDataFetched(prev => ({ ...prev, emailLogs: true }));
                } catch (error) {
                    console.error('Error fetching email logs:', error);
                } finally {
                    setEmailLogsLoading(false);
                }
            }
        };

        fetchEmailLogs();
    }, [id, candidate, dataFetched.emailLogs]);

    const refreshActivities = useCallback(() => {
        if (id) {
            dispatch(fetchCandidateActivities(id));
        }
    }, [id, dispatch]);

    const refreshEmailLogs = useCallback(async () => {
        if (id) {
            try {
                const logs = await emailTemplateService.getEmailLogs(id);
                setEmailLogs(logs);
            } catch (error) {
                console.error('Error refreshing email logs:', error);
            }
        }
    }, [id]);

    const handleFileChange = (files: File[]) => {
        setSelectedFiles(files);
    };

    const handleUploadDocuments = async () => {
        if (!id || selectedFiles.length === 0) return;

        setUploadingDocuments(true);
        try {
            for (const file of selectedFiles) {
                await dispatch(uploadCandidateDocument({ candidateId: id, file })).unwrap();
            }
            setSelectedFiles([]);
            refreshActivities();
            setToastVariant('success');
            setToastMessage(`${selectedFiles.length} document(s) uploaded successfully`);
            setShowToast(true);
        } catch (error) {
            console.error('Error uploading documents:', error);
            setToastVariant('danger');
            setToastMessage('Failed to upload documents. Please try again.');
            setShowToast(true);
        } finally {
            setUploadingDocuments(false);
        }
    };

    const handleDocumentDelete = async (documentId: string) => {
        if (!id) return;

        try {
            await dispatch(deleteCandidateDocument({ candidateId: id, documentId })).unwrap();
            refreshActivities();
            setToastVariant('success');
            setToastMessage('Document deleted successfully');
            setShowToast(true);
        } catch (error) {
            console.error('Error deleting document:', error);
            setToastVariant('danger');
            setToastMessage('Failed to delete document. Please try again.');
            setShowToast(true);
        }
    };

    const handleCandidateUpdate = async (updatedCandidateData: any) => {
        if (!id) return;

        try {
            await dispatch(updateCandidate({ id, candidateData: updatedCandidateData })).unwrap();
            setShowEditForm(false);
            refreshActivities();
            setToastVariant('success');
            setToastMessage('Candidate updated successfully');
            setShowToast(true);
        } catch (error) {
            console.error('Error updating candidate:', error);
            setToastVariant('danger');
            setToastMessage('Failed to update candidate. Please try again.');
            setShowToast(true);
        }
    };

    const handleStageChange = async (candidateId: string, newStageId: string, notes?: string) => {
        if (!candidate) return;

        try {
            const oldStageId = candidate.stageId;

            await dispatch(changeCandidateStage({
                candidateId,
                stageChangeData: { candidateId, newStageId, notes }
            })).unwrap();

            if (oldStageId !== newStageId) {
                dispatch(updateStageCount({
                    fromStageId: oldStageId,
                    toStageId: newStageId
                }));
            }

            const stageData = await stageService.getStageById(newStageId);
            setCurrentStage(stageData);


            setToastVariant('success');
            setToastMessage('Candidate stage updated successfully');
            setShowToast(true);

            refreshActivities();
            await refreshEmailLogs();
        } catch (error) {
            console.error('Error changing candidate stage:', error);
            setToastVariant('danger');
            setToastMessage('Failed to change candidate stage. Please try again.');
            setShowToast(true);
        }
    };

    const handleEmailSent = async () => {
        setShowEmailModal(false);
        refreshActivities();
        await refreshEmailLogs();
        setToastVariant('success');
        setToastMessage('Email sent successfully');
        setShowToast(true);
    };

    const handleViewEmail = async (emailLog: EmailLog) => {
        try {
            setSelectedEmailLog(emailLog);
            setEmailPreviewLoading(true);

            const sentEmail = await emailTemplateService.getSentEmail(emailLog.id);
            setEmailPreviewHtml(sentEmail.htmlContent);
            setShowEmailPreviewModal(true);
        } catch (error) {
            console.error('Error retrieving sent email:', error);

            try {
                if (candidate) {
                    const previewHtml = await emailTemplateService.previewEmail(
                        emailLog.templateId,
                        candidate.id
                    );
                    setEmailPreviewHtml(previewHtml);
                    setShowEmailPreviewModal(true);
                }
            } catch (fallbackError) {
                console.error('Error generating fallback email preview:', fallbackError);
                setToastVariant('danger');
                setToastMessage('Failed to load email content');
                setShowToast(true);
            }
        } finally {
            setEmailPreviewLoading(false);
        }
    };

    const handleAddNote = async (candidateId: string, text: string): Promise<Note> => {
        try {
            const result = await dispatch(addCandidateNote({ candidateId, text })).unwrap();
            refreshActivities();
            setToastVariant('success');
            setToastMessage('Note added successfully');
            setShowToast(true);
            return result.note;
        } catch (error) {
            setToastVariant('danger');
            setToastMessage('Failed to add note. Please try again.');
            setShowToast(true);
            throw error;
        }
    };

    const handleEditNote = async (noteId: string, text: string): Promise<void> => {
        try {
            await dispatch(updateCandidateNote({ noteId, text })).unwrap();
            refreshActivities();
            setToastVariant('success');
            setToastMessage('Note updated successfully');
            setShowToast(true);
        } catch (error) {
            setToastVariant('danger');
            setToastMessage('Failed to update note. Please try again.');
            setShowToast(true);
            throw error;
        }
    };

    const handleDeleteNote = async (noteId: string): Promise<void> => {
        if (!id) return;

        try {
            await dispatch(deleteCandidateNote({ candidateId: id, noteId })).unwrap();
            refreshActivities();
            setToastVariant('success');
            setToastMessage('Note deleted successfully');
            setShowToast(true);
        } catch (error) {
            setToastVariant('danger');
            setToastMessage('Failed to delete note. Please try again.');
            setShowToast(true);
            throw error;
        }
    };

    const formatDateTime = (date: Date) => {
        return new Date(date).toLocaleString('en-GB', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Handle loading states
    if (candidateLoading) {
        return (
            <Container className="py-5">
                <LoadingSpinner centered />
                <div className="text-center mt-3">
                    <small className="text-muted">Loading candidate {id}...</small>
                </div>
            </Container>
        );
    }

    // Handle errors
    if (candidateError) {
        return (
            <Container className="py-5">
                <Alert variant="danger">
                    <strong>Error loading candidate:</strong> {candidateError}
                </Alert>
                <Button variant="secondary" onClick={() => navigate('/candidates')}>
                    Back to Candidates
                </Button>
            </Container>
        );
    }

    if (!candidate && !candidateLoading && dataFetched.candidate) {
        return (
            <Container className="py-5">
                <Alert variant="warning">
                    <strong>Candidate not found.</strong>
                </Alert>
                <Button variant="secondary" onClick={() => navigate('/candidates')}>
                    Back to Candidates
                </Button>
                <Button variant="primary" className="ms-2" onClick={() => {
                    setDataFetched({ candidate: false });
                }}>
                    Retry Fetch
                </Button>
            </Container>
        );
    }

    // If we don't have a candidate yet and we're not loading, show loading
    if (!candidate) {
        return (
            <Container className="py-5">
                <LoadingSpinner centered />
                <div className="text-center mt-3">
                    <small className="text-muted">
                        Waiting for candidate data...
                        {dataFetched.candidate && ' (Data fetched but not in Redux store)'}
                    </small>
                </div>
            </Container>
        );
    }

    return (
        <Container className="py-4">
            {/* Toast Notification */}
            <Toast
                show={showToast}
                onClose={() => setShowToast(false)}
                delay={3000}
                autohide
                style={{
                    position: 'fixed',
                    top: 20,
                    right: 20,
                    zIndex: 9999
                }}
                bg={toastVariant}
            >
                <Toast.Header closeButton>
                    <strong className="me-auto">
                        {toastVariant === 'success' ? 'Success' : 'Error'}
                    </strong>
                </Toast.Header>
                <Toast.Body className={toastVariant === 'success' ? 'text-white' : ''}>
                    {toastMessage}
                </Toast.Body>
            </Toast>

            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <Button
                                variant="outline-secondary"
                                className="me-2"
                                onClick={() => navigate(backTo || `/candidates/stage/${candidate.stageId}`)}
                            >
                                <i className="bi bi-arrow-left me-1"></i> Back
                            </Button>
                            <h1 className="d-inline-block mb-0">
                                {candidate.firstName} {candidate.lastName}
                            </h1>
                        </div>
                        <div>
                            <Button
                                variant="outline-info"
                                className="me-2"
                                onClick={() => setShowEmailModal(true)}
                            >
                                <i className="bi bi-envelope me-1"></i> Send Email
                            </Button>
                            <Button
                                variant="outline-primary"
                                className="me-2"
                                onClick={() => setShowEditForm(true)}
                            >
                                <i className="bi bi-pencil me-1"></i> Edit
                            </Button>
                            <AdvanceStageButton
                                candidateId={candidate.id}
                                currentStageId={candidate.stageId}
                                onStageChange={handleStageChange}
                            />
                        </div>
                    </div>
                </Col>
            </Row>

            {error && (
                <Row className="mb-4">
                    <Col>
                        <Alert variant="danger" onClose={() => setError(null)} dismissible>
                            {error}
                        </Alert>
                    </Col>
                </Row>
            )}

            {showEditForm ? (
                <Card className="mb-4">
                    <Card.Header>
                        <div className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Edit Candidate</h5>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setShowEditForm(false)}
                            >
                                <i className="bi bi-x"></i> Cancel
                            </Button>
                        </div>
                    </Card.Header>
                    <Card.Body>
                        <CandidateForm
                            candidate={candidate}
                            onSave={handleCandidateUpdate}
                            onCancel={() => setShowEditForm(false)}
                        />
                    </Card.Body>
                </Card>
            ) : (
                <Card className="mb-4">
                    <Card.Body>
                        <Row>
                            <Col md={4}>
                                <p className="text-muted mb-1">Email</p>
                                <p className="mb-3">{candidate.email}</p>

                                {candidate.phone && (
                                    <>
                                        <p className="text-muted mb-1">Phone</p>
                                        <p className="mb-3">{candidate.phone}</p>
                                    </>
                                )}

                                {candidate.location && (
                                    <>
                                        <p className="text-muted mb-1">Location</p>
                                        <p className="mb-3">{candidate.location}</p>
                                    </>
                                )}
                            </Col>
                            <Col md={4}>
                                <p className="text-muted mb-1">Current Stage</p>
                                <p className="mb-3">
                                    <span className="badge bg-primary">{candidate.currentStage}</span>
                                </p>

                                <p className="text-muted mb-1">Days in Stage</p>
                                <p className="mb-3">{candidate.daysInCurrentStage}</p>

                                {candidate.jobTitle && (
                                    <>
                                        <p className="text-muted mb-1">Job</p>
                                        <p className="mb-3">{candidate.jobTitle}</p>
                                    </>
                                )}
                            </Col>
                            <Col md={4}>
                                <p className="text-muted mb-1">Created</p>
                                <p className="mb-3">{formatDateTime(candidate.createdAt)}</p>

                                {candidate.source && (
                                    <>
                                        <p className="text-muted mb-1">Source</p>
                                        <p className="mb-3">{candidate.source}</p>
                                    </>
                                )}

                                {candidate.interviewDate && (
                                    <>
                                        <p className="text-muted mb-1">Interview Date</p>
                                        <p className="mb-3">{formatDateTime(candidate.interviewDate)}</p>
                                    </>
                                )}
                            </Col>
                        </Row>

                        {candidate.notes && (
                            <div className="mt-3">
                                <h6>Notes</h6>
                                <p className="mb-0">{candidate.notes}</p>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            )}

            <Tabs defaultActiveKey="documents" id="candidate-tabs" className="mb-4">
                <Tab eventKey="documents" title="Documents">
                    <Card>
                        <Card.Body>
                            <h5 className="mb-4">Documents</h5>

                            <DocumentUploader
                                onFileChange={handleFileChange}
                                maxFiles={10}
                                existingDocuments={documents.map(doc => ({
                                    id: doc.id,
                                    name: doc.fileName
                                }))}
                            />

                            {selectedFiles.length > 0 && (
                                <div className="d-grid mt-3">
                                    <Button
                                        variant="primary"
                                        onClick={handleUploadDocuments}
                                        disabled={uploadingDocuments}
                                    >
                                        {uploadingDocuments ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-cloud-upload me-2"></i>
                                                Upload {selectedFiles.length} File(s)
                                            </>
                                        )}
                                    </Button>
                                </div>
                            )}

                            <hr className="my-4" />

                            <h6>Uploaded Documents</h6>
                            {documentsLoading ? (
                                <div className="text-center py-3">
                                    <LoadingSpinner />
                                </div>
                            ) : (
                                <DocumentList
                                    documents={documents}
                                    candidateId={candidate.id}
                                    onDelete={handleDocumentDelete}
                                />
                            )}
                        </Card.Body>
                    </Card>
                </Tab>

                <Tab eventKey="notes" title="Notes">
                    <Card>
                        <Card.Body>
                            {notesLoading ? (
                                <div className="text-center py-3">
                                    <LoadingSpinner />
                                </div>
                            ) : (
                                <NotesList
                                    notes={notes}
                                    candidateId={candidate.id}
                                    onAddNote={handleAddNote}
                                    onEditNote={handleEditNote}
                                    onDeleteNote={handleDeleteNote}
                                />
                            )}
                        </Card.Body>
                    </Card>
                </Tab>

                <Tab eventKey="activities" title="Activities">
                    <Card>
                        <Card.Body>
                            <h5 className="mb-4">Activity History</h5>

                            {activitiesLoading ? (
                                <div className="text-center py-3">
                                    <Spinner animation="border" size="sm" />
                                </div>
                            ) : (
                                <ActivityTimeline activities={activities} />
                            )}
                        </Card.Body>
                    </Card>
                </Tab>

                <Tab eventKey="communications" title="Emails">
                    <Card>
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h5 className="mb-0">Emails</h5>
                            </div>

                            {emailLogsLoading ? (
                                <div className="text-center py-3">
                                    <Spinner animation="border" size="sm" />
                                </div>
                            ) : emailLogs.length > 0 ? (
                                <div className="timeline">
                                    {emailLogs.map((log, index) => (
                                        <div key={index} className="timeline-item mb-4">
                                            <div className="d-flex">
                                                <div className="me-3">
                                                    <div
                                                        className={`rounded-circle ${log.successful ? 'bg-success' : 'bg-warning'} text-white d-flex align-items-center justify-content-center`}
                                                        style={{ width: '40px', height: '40px', flexShrink: 0 }}
                                                    >
                                                        <i className={`bi ${log.successful ? 'bi-envelope-check' : 'bi-envelope-exclamation'}`}></i>
                                                    </div>
                                                    {index < emailLogs.length - 1 && (
                                                        <div
                                                            className="timeline-connector"
                                                            style={{
                                                                width: '2px',
                                                                height: '40px',
                                                                backgroundColor: '#dee2e6',
                                                                margin: '4px auto'
                                                            }}
                                                        ></div>
                                                    )}
                                                </div>
                                                <div className="timeline-content" style={{ flex: 1 }}>
                                                    <h6 className="mb-1">{log.subject}</h6>
                                                    <p className="text-muted small mb-2">
                                                        <strong>To:</strong> {log.toEmail} <br />
                                                        <strong>Sent:</strong> {new Date(log.sentAt).toLocaleString()} <br />
                                                        <strong>By:</strong> {log.sentByUsername} <br />
                                                        <strong>Template:</strong> {log.templateName}
                                                    </p>
                                                    <div>
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            onClick={() => handleViewEmail(log)}
                                                        >
                                                            <i className="bi bi-eye me-1"></i> View Email
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <i className="bi bi-envelope-x text-muted display-4"></i>
                                    <p className="text-muted mt-2">No email communications yet.</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Tab>
            </Tabs>

            {/* Email Modal */}
            {
                candidate && (
                    <SendEmailModal
                        show={showEmailModal}
                        onHide={() => setShowEmailModal(false)}
                        candidateId={candidate.id}
                        candidateName={`${candidate.firstName} ${candidate.lastName}`}
                        candidateEmail={candidate.email}
                        stageId={candidate.stageId}
                        onEmailSent={handleEmailSent}
                    />
                )
            }

            {/* Email Preview Modal */}
            <Modal
                show={showEmailPreviewModal}
                onHide={() => setShowEmailPreviewModal(false)}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="bi bi-envelope me-2"></i>
                        Email Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {emailPreviewLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" />
                            <p className="mt-2">Loading email content...</p>
                        </div>
                    ) : (
                        <div>
                            {selectedEmailLog && (
                                <Card className="mb-3">
                                    <Card.Header>
                                        <h5 className="mb-0">{selectedEmailLog.subject}</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row>
                                            <Col md={6}>
                                                <p><strong>To:</strong> {selectedEmailLog.toEmail}</p>
                                                <p><strong>Sent By:</strong> {selectedEmailLog.sentByUsername}</p>
                                                <p><strong>Template:</strong> {selectedEmailLog.templateName}</p>
                                            </Col>
                                            <Col md={6}>
                                                <p><strong>Date:</strong> {new Date(selectedEmailLog.sentAt).toLocaleString()}</p>
                                                <p>
                                                    <strong>Status:</strong>{' '}
                                                    <span className={`badge ${selectedEmailLog.successful ? 'bg-success' : 'bg-danger'}`}>
                                                        {selectedEmailLog.successful ? 'Delivered Successfully' : 'Delivery Failed'}
                                                    </span>
                                                </p>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            )}

                            <div
                                className="email-content border rounded p-3 bg-light"
                                style={{ minHeight: '300px', maxHeight: '500px', overflow: 'auto' }}
                                dangerouslySetInnerHTML={{ __html: emailPreviewHtml }}
                            />
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEmailPreviewModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container >
    );
};

export default ViewCandidatePage;